import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    IconCheck,
    IconChevronDown,
    IconHelpCircle,
    IconHome,
    IconPlus,
    IconShare2,
    IconStar,
    IconStarFilled,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { useToolFavoritesStore } from '../../../../../store/toolFavoritesStore';
import { useToolUsageStore } from '../../../../../store/toolUsageStore';
import {
    CATS,
    SITE_URL,
    getRelatedTools,
    getToolBySlug,
    isInternalTool,
    isPaidTool,
    toolIconSrc,
} from '../../toolCatalog';
import { squircleStyle, tileGradient } from '../../toolTile';
import { copyTextToClipboard } from '../../toolPageUtils';
import ToolCard from './ToolCard';
import ToolActionBar from './ToolActionBar';
import ToolTrustBanner from './ToolTrustBanner';
import { ToolResultBarContext } from './toolResultBar';

/**
 * Shared shell for tool pages — matches Sohozkaj Dashboard design system.
 */
export default function ToolPageLayout({
    slug,
    subtitleKey,
    /** Credits one run of this tool costs. Unset/0 = free, the usual case. */
    creditCost = null,
    status = 'empty',
    statusText,
    controlsTitle,
    showAdd = false,
    onAdd,
    addLabel,
    controls,
    settings,
    /** Buttons placed left of favourite/share in the page header. */
    headerActions,
    preview,
    /** Card pinned under the preview rail ('wideControls' variant only). */
    previewFooter,
    belowWorkArea,
    howToSteps = [],
    primaryAction,
    /** Optional outlined button rendered next to the primary one in the action bar. */
    secondaryAction,
    /** A control that belongs with the run button — an output-format picker, say. */
    actionExtra,
    primaryActionPlacement = 'default',
    onReset,
    resetLabel,
    showTrust = true,
    /** Pin preview height to the left sidebar and scroll content inside. */
    matchSidebarHeight = false,
    /** When matching, grow the work area to at least this CSS min-height (e.g. min(780px,82vh)). */
    matchSidebarMinHeight,
    /** Stretch the left controls card to the preview panel height (file list scrolls inside). */
    matchPreviewHeight = false,
    /** Drop the left controls column so the preview takes the full width (default variant only). */
    hideControls = false,
    /**
     * 'wideControls' flips the work area: controls take the remaining width and
     * the preview becomes a narrow sticky rail on the right (preview first on
     * mobile). `controls` is rendered as-is, without the shared ToolCard.
     */
    variant = 'default',
    /**
     * Desktop width of that rail ('wideControls' only). The controls column takes
     * whatever is left, so a percentage here has to allow for the 18px gap —
     * `lg:w-[calc(40%_-_9px)]` is the honest way to write a 60/40 split.
     */
    railWidthClass = 'lg:w-[400px]',
    /**
     * Whether the preview rail leads on mobile ('wideControls' only). True suits
     * a tool whose artwork is the thing being judged from the first tap; a tool
     * you have to type into first wants the controls up top instead.
     */
    mobilePreviewFirst = true,
    /**
     * Pin the action bar to the bottom of the screen instead of placing it under
     * the work area. On for every tool category (slug `pdf/…`, `image/…`,
     * `text/…`, `video/…`, `general/…`, `result/…`) — each ends in a single run,
     * search or download button that otherwise scrolls away under long file
     * lists, previews, players, printed forms and output boxes. A tool with no
     * `primaryAction` has no bar, so the live calculators and text tools are
     * unaffected; a sticky bar also overrides `primaryActionPlacement` (see
     * below), since pinned to the screen it is next to the form anyway. Pass it
     * explicitly to opt another tool in or out.
     */
    stickyActionBar = /^(pdf|image|text|video|general|result)\//.test(String(slug || '')),
    /**
     * Once a run finishes, the preview panel hands its download, print and
     * edit-again buttons to the sticky bar instead of drawing them under the
     * preview (see toolResultBar). On for the PDF and video tools.
     */
    resultActionsInBar = /^(pdf|video)\//.test(String(slug || '')),
}) {
    const navigate = useNavigate();
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';

    const tool = getToolBySlug(slug);
    const cat = CATS[tool?.categoryId] || CATS.pdf;
    const CatIcon = cat.Icon;

    // These categories drop the trust strip and the how-to accordion — the page
    // ends at the work area and the related-tools row. Keyed off the category, not
    // the slug, so a tool routed outside its category folder (form-auto-fillup is
    // an education tool at /tools/form-auto-fillup) is judged by what it IS. Only
    // the image tools still carry them; `showTrust={false}` drops the strip on one
    // of those.
    const BLURBLESS_CATEGORIES = ['pdf', 'text', 'video', 'general', 'result'];
    const hidesBlurb = BLURBLESS_CATEGORIES.includes(
        // A page the catalogue doesn't know (a tool being built, a renamed slug)
        // still answers from its route.
        tool?.categoryId ?? String(slug || '').split('/')[0],
    );

    const favorites = useToolFavoritesStore((s) => s.favorites);
    const loadFavorites = useToolFavoritesStore((s) => s.load);
    const toggleFav = useToolFavoritesStore((s) => s.toggle);
    const isFav = favorites.has(slug);

    const [howToOpen, setHowToOpen] = useState(false);
    const sharingRef = useRef(false);

    const loadUsage = useToolUsageStore((s) => s.load);
    // COMBINED all-time runs of this tool across every user (matches the superadmin "total" column).
    // Hidden until it has loaded, so a stale/personal number is never shown.
    const loadTotals = useToolUsageStore((s) => s.loadTotals);
    const totalsLoaded = useToolUsageStore((s) => s.totalsLoaded);
    const usageCount = useToolUsageStore((s) => s.totals[slug] ?? 0);

    useEffect(() => { loadFavorites(); }, [loadFavorites]);
    useEffect(() => { loadUsage(); }, [loadUsage]);
    useEffect(() => { loadTotals(); }, [loadTotals]);
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [slug]);

    const title = tool ? (isBn ? tool.nameBn : tool.name) : slug;
    const isPaid = tool ? isPaidTool(tool) : false; // the AI tools: they cost credits, so they are never "free"
    const categoryLabel = tool ? (isBn ? tool.categoryTitleBn : tool.categoryTitle) : '';
    const related = useMemo(() => getRelatedTools(slug, 6), [slug]);

    const addButton = showAdd && onAdd ? (
        <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1 rounded-lg border-none bg-pdf-cat-tint px-2.5 py-1.5 text-[12.5px] font-semibold text-pdf-cat-solid transition-colors hover:bg-[#ffd9cf] dark:bg-orange-950/30 dark:text-orange-400"
        >
            <IconPlus size={14} stroke={2.2} />
            {addLabel || t('toolPage.add')}
        </button>
    ) : null;

    const showShareCopied = () => {
        notifications.show({
            title: t('common.success'),
            message: t('toolPage.shareLinkCopied'),
            color: 'green',
        });
    };

    const handleShare = () => {
        if (sharingRef.current) return;
        sharingRef.current = true;

        const url = window.location.href;
        const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

        const finishCopy = () => {
            if (copyTextToClipboard(url)) {
                showShareCopied();
                sharingRef.current = false;
                return;
            }

            void navigator.clipboard?.writeText(url)
                .then(showShareCopied, () => {
                    notifications.show({
                        title: t('common.error'),
                        message: t('toolPage.shareFailed'),
                        color: 'red',
                    });
                })
                .finally(() => {
                    sharingRef.current = false;
                });
        };

        if (isMobile && navigator.share) {
            const payload = { title, url };
            if (!navigator.canShare || navigator.canShare(payload)) {
                navigator.share(payload)
                    .catch((err) => {
                        if (err?.name !== 'AbortError') finishCopy();
                    })
                    .finally(() => {
                        sharingRef.current = false;
                    });
                return;
            }
        }

        finishCopy();
    };

    const openRelated = (relatedTool) => {
        if (isInternalTool(relatedTool)) navigate(relatedTool.path);
        else window.open(SITE_URL + relatedTool.path, '_blank', 'noopener,noreferrer');
    };

    // Filled by ToolPreviewPanel while it shows a finished result: the run button
    // then becomes download, with print / edit again beside it.
    const [resultActions, setResultActions] = useState(null);
    const resultBar = useMemo(
        () => (resultActionsInBar && stickyActionBar ? { setResultActions } : null),
        [resultActionsInBar, stickyActionBar],
    );
    const showResultActions = !!resultBar && !!resultActions;
    const barPrimary = showResultActions ? resultActions.primary : primaryAction;

    const actionBar = barPrimary ? (
        <ToolActionBar
            status={status}
            statusText={statusText}
            onReset={onReset}
            resetLabel={resetLabel}
            primaryLabel={barPrimary.label}
            onPrimary={barPrimary.onClick}
            primaryDisabled={barPrimary.disabled}
            primaryLoading={barPrimary.loading}
            primaryIcon={barPrimary.icon}
            secondaryAction={showResultActions ? resultActions.secondary : secondaryAction}
            actionExtra={showResultActions ? null : actionExtra}
            sticky={stickyActionBar}
        />
    ) : null;

    // A sticky bar sits straight after the work area whatever the placement asks
    // for. `position: sticky` pins it to the bottom of the screen only while its
    // spot in the page is still below the fold — so while the tool is in view it
    // rides along, and once the page is scrolled past the tool it comes to rest
    // there, above the how-to and related tools instead of covering them.
    const sticky = stickyActionBar && !!actionBar;
    const showActionBeforeBelowWorkArea = !sticky && primaryActionPlacement === 'beforeBelowWorkArea';

    return (
        <ToolResultBarContext.Provider value={resultBar}>
        <div className="relative -mx-4 px-4 pb-8 pt-2 font-hind dark:bg-zinc-950 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            {/* 1. Breadcrumb */}
            <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-[13px] text-[#6B635C] dark:text-zinc-500">
                <Link
                    to="/"
                    className="inline-flex items-center transition-colors hover:text-primary"
                    aria-label={t('toolPage.breadcrumb.home')}
                >
                    <IconHome size={15} stroke={1.8} />
                </Link>
                <span className="text-[#A8A099]">/</span>
                <Link to="/tools" className="transition-colors hover:text-primary">
                    {t('toolPage.breadcrumb.tools')}
                </Link>
                {categoryLabel && tool?.categoryId ? (
                    <>
                        <span className="text-[#A8A099]">/</span>
                        <Link
                            to={`/tools?tab=${tool.categoryId}`}
                            className="transition-colors hover:text-primary"
                        >
                            {categoryLabel}
                        </Link>
                    </>
                ) : null}
                <span className="text-[#A8A099]">/</span>
                <span className="font-semibold text-[#1C1815] dark:text-zinc-100">{title}</span>
            </nav>

            {/* 2. Tool header */}
            <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3.5">
                    <div className={`flex h-[54px] w-[54px] flex-none items-center justify-center rounded-[15px] ${cat.chip}`}>
                        {tool?.iconImg ? (
                            <img src={toolIconSrc(tool.iconImg)} alt="" className="h-7 w-7 object-contain" />
                        ) : (
                            <CatIcon size={26} stroke={1.7} className={cat.accent} />
                        )}
                    </div>
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="font-hind text-[28px] font-bold leading-tight text-[#1C1815] dark:text-zinc-100">
                                {title}
                            </h1>
                            {/* Nearly every tool runs in the browser and is free; the
                                few that call the AI on the server are paid: they say so
                                here (same badge as on the tools grid) and price a run in
                                credits once the cost is known — never "free", not even
                                while that cost is still loading. */}
                            {isPaid && (
                                <span className="rounded-full bg-amber-700 px-3.5 py-1 text-[14px] font-semibold text-white">
                                    {t('toolsPage.badge.paid')}
                                </span>
                            )}
                            {creditCost ? (
                                <span className="rounded-full bg-amber-100 px-3.5 py-1 text-[14px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                    {t('toolPage.creditBadge', { count: toBnDigits(creditCost) })}
                                </span>
                            ) : !isPaid && (
                                <span className="rounded-full px-3.5 py-1 text-[14px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                    {t('toolsPage.freeBadge') || 'ফ্রি'}
                                </span>
                            )}
                            {totalsLoaded && (
                                <span className="flex items-center gap-1.5 text-[12.5px] text-[#A8A099] dark:text-zinc-500">
                                    <IconCheck size={13} stroke={1.9} />
                                    {t('toolPage.usageCount', { count: toBnDigits(usageCount) })}
                                </span>
                            )}
                        </div>
                        <p className="mt-0.5 text-[15px] text-[#6B635C] dark:text-zinc-400">
                            {subtitleKey ? t(subtitleKey) : ''}
                        </p>
                    </div>
                </div>
                <div className="flex flex-none items-center gap-2">
                    {headerActions}
                    <button
                        type="button"
                        onClick={() => toggleFav(slug)}
                        className="flex h-[42px] w-[42px] items-center justify-center rounded-xl border border-[#ECE8E4] bg-white transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900"
                        aria-label={t('toolPage.favorite')}
                    >
                        {isFav ? (
                            <IconStarFilled size={18} className="text-primary" />
                        ) : (
                            <IconStar size={18} stroke={1.7} className="text-[#A8A099]" />
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={handleShare}
                        className="flex h-[42px] w-[42px] items-center justify-center rounded-xl border border-[#ECE8E4] bg-white text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                        aria-label={t('toolPage.share')}
                    >
                        <IconShare2 size={17} stroke={1.8} />
                    </button>
                    {/* Without an action bar the reset control has to live up here;
                        with one, রিসেট is in the bar and a second copy would be noise. */}
                    {variant === 'wideControls' && onReset && !actionBar ? (
                        <button
                            type="button"
                            onClick={onReset}
                            className="h-[42px] rounded-xl border border-[#ECE8E4] bg-white px-4 text-[13.5px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                        >
                            {resetLabel || t('toolPage.reset')}
                        </button>
                    ) : null}
                </div>
            </div>

            {/* 3. Work area */}
            {variant === 'wideControls' ? (
                // Controls first on mobile — these tools are built by typing and
                // picking, so leading with an empty preview pushes the one thing
                // the page is for below the fold. On desktop the preview becomes
                // the sticky rail on the right and stays in view regardless.
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-[18px]">
                    <div className="flex min-w-0 flex-1 flex-col gap-3.5">
                        {controls}
                        {settings}
                    </div>
                    {preview || previewFooter ? (
                        // self-stretch gives the rail the full row height, which is
                        // the room the sticky preview needs to stay pinned.
                        <div className={`flex w-full flex-none flex-col gap-3.5 lg:self-stretch ${railWidthClass}`}>
                            {/* The page decides what pins: anything taller than the
                                viewport cannot stick, so the caller marks the one
                                card worth keeping in view. */}
                            {preview}
                            {previewFooter}
                        </div>
                    ) : null}
                </div>
            ) : (
            <div
                className={matchSidebarHeight
                    ? 'relative flex flex-col gap-4 lg:block'
                    : matchPreviewHeight
                        ? 'flex flex-col gap-4 lg:grid lg:h-[min(992px,calc(90vh+72px))] lg:grid-cols-[380px_minmax(0,1fr)] lg:gap-[18px]'
                        : 'flex flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-[18px]'
                }
                style={matchSidebarHeight && matchSidebarMinHeight
                    ? { minHeight: matchSidebarMinHeight }
                    : undefined
                }
            >
                {hideControls ? null : (
                <div
                    className={`order-1 flex w-full flex-none flex-col gap-3.5 ${
                        matchPreviewHeight
                            ? 'min-h-0 lg:h-full lg:w-auto lg:overflow-hidden'
                            : 'lg:w-[380px]'
                    }`}
                >
                    {/* A tool that collects its files in the preview itself passes no
                        controls; rendering the empty card would leave a stray box. */}
                    {controls ? (
                        <ToolCard
                            title={controlsTitle}
                            headerAction={addButton}
                            className={matchPreviewHeight
                                ? 'flex h-full min-h-0 flex-1 flex-col overflow-hidden'
                                : ''
                            }
                        >
                            {matchPreviewHeight ? (
                                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                    {controls}
                                </div>
                            ) : (
                                controls
                            )}
                        </ToolCard>
                    ) : null}
                    {settings}
                </div>
                )}
                <div
                    className={matchSidebarHeight
                        ? 'order-2 flex min-h-[min(560px,70vh)] min-w-0 flex-1 flex-col lg:absolute lg:bottom-0 lg:left-[calc(380px+18px)] lg:right-0 lg:top-0 lg:min-h-0'
                        : matchPreviewHeight
                            ? 'order-2 flex min-h-[min(560px,70vh)] min-w-0 flex-1 flex-col lg:h-full lg:min-h-0 lg:overflow-hidden'
                            : 'order-2 flex min-h-0 min-w-0 flex-1 flex-col'
                    }
                >
                    {preview}
                </div>
            </div>
            )}

            {showActionBeforeBelowWorkArea ? (
                <div className="mt-4 w-full">
                    {actionBar}
                </div>
            ) : null}

            {belowWorkArea ? (
                <div className="mt-4 w-full">
                    {belowWorkArea}
                </div>
            ) : null}

            {/* 4. Action bar (sticky or not) + trust strip */}
            {!showActionBeforeBelowWorkArea ? actionBar : null}
            {showTrust && !hidesBlurb ? <ToolTrustBanner /> : null}

            {/* 5. How-to accordion */}
            {howToSteps.length > 0 && !hidesBlurb && (
                <div className="mt-4 overflow-hidden rounded-[20px] border border-[#ECE8E4] bg-white shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
                    <button
                        type="button"
                        onClick={() => setHowToOpen((v) => !v)}
                        className="flex w-full items-center justify-between gap-2.5 px-[18px] py-[15px] text-left"
                    >
                        <span className="flex items-center gap-2 text-[14.5px] font-semibold text-[#1C1815] dark:text-zinc-100">
                            <IconHelpCircle size={18} className="text-pdf-cat-solid" stroke={1.8} />
                            {t('toolPage.howToTitle')}
                        </span>
                        <IconChevronDown
                            size={18}
                            className={`flex-shrink-0 text-[#A8A099] transition-transform ${howToOpen ? 'rotate-180' : ''}`}
                        />
                    </button>
                    {howToOpen && (
                        <div className="flex flex-col gap-3 px-[18px] pb-[18px]">
                            {howToSteps.map((step, index) => (
                                <div key={step} className="flex items-start gap-2.5">
                                    <span className="flex h-[25px] w-[25px] flex-none items-center justify-center rounded-lg bg-pdf-cat-tint text-xs font-bold text-pdf-cat-solid dark:bg-orange-950/30 dark:text-orange-400">
                                        {toBnDigits(index + 1)}
                                    </span>
                                    <span className="pt-px text-[13.5px] leading-relaxed text-[#3D3833] dark:text-zinc-300">
                                        {step}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* 6. Related tools */}
            {related.length > 0 && (
                // Set apart from the work area above it: this is a nav strip, not
                // part of the tool, and at mt-4 it read as another of its panels.
                <div className="mt-16 border-t border-[#ECE8E4] pt-8 dark:border-zinc-800">
                    <h2 className="mb-2.5 text-sm font-semibold text-[#1C1815] dark:text-zinc-100">
                        {t('toolPage.relatedTitle')}
                    </h2>
                    {/* The /tools grid, tile for tile: the same auto-filled columns,
                        the same 150px cap on the icon and the same caption size, so
                        the strip reads as a slice of that page. */}
                    <div className="grid grid-cols-4 gap-x-2 gap-y-3 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] sm:gap-x-3.5 sm:gap-y-6">
                        {related.map((relatedTool) => {
                            const relTitle = isBn ? relatedTool.nameBn : relatedTool.name;
                            const RelIcon = relatedTool.IconComp || (CATS[relatedTool.categoryId] || CATS.pdf).Icon;
                            return (
                                <button
                                    key={relatedTool.slug}
                                    type="button"
                                    onClick={() => openRelated(relatedTool)}
                                    className="group flex flex-col items-center rounded-xl px-0 py-1.5 text-center transition-colors hover:bg-white dark:hover:bg-zinc-800 sm:rounded-3xl sm:px-2 sm:py-5"
                                >
                                    <div
                                        style={{ ...squircleStyle, backgroundImage: tileGradient(relatedTool) }}
                                        className="flex aspect-square w-full items-center justify-center drop-shadow-lg transition-transform duration-150 group-hover:scale-[1.03] sm:w-[150px]"
                                    >
                                        {relatedTool.iconImg ? (
                                            <img src={toolIconSrc(relatedTool.iconImg)} alt="" className="h-3/5 w-3/5 object-contain brightness-0 invert sm:h-[112px] sm:w-[112px]" loading="lazy" />
                                        ) : (
                                            <RelIcon stroke={1.6} className="h-3/5 w-3/5 text-white" />
                                        )}
                                    </div>
                                    {/* A div, as on /tools: global.scss forces every <p> to weight
                                        400 !important, which thinned these captions. */}
                                    <div className="mt-1 line-clamp-2 w-full px-0.5 text-[9px] font-semibold leading-tight text-zinc-800 dark:text-zinc-100 sm:mt-4 sm:max-w-[170px] sm:text-[15.5px]">
                                        {relTitle}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

        </div>
        </ToolResultBarContext.Provider>
    );
}
